// What the game says to the players, over the fight.
//
// Two things, and they are one voice. The round announcement, ROUND n and then
// FIGHT, straight off the cabinet, which exists for the beat it puts between the
// city appearing and the first shot being fired — that beat is the whole trick, it
// is what turns a screen of rooftops into a fight that is about to start, and
// makeCity plays the intro fanfare at that exact moment so the two land together.
//
// And the nudge that tells whoever's turn it has just become that it is theirs.
// Said once a turn instead of once a round, so it is dressed accordingly — smaller,
// no wash — and it waits for the round call rather than talking over it.

import { ctx, W, H } from '../canvas.js';
import { state } from '../state.js';
import { t } from '../i18n.js';
import {
  ANNOUNCE_FIGHT_FONT_PX,
  ANNOUNCE_FIGHT_MS,
  ANNOUNCE_FONT_PX,
  ANNOUNCE_ROUND_MS,
  ANNOUNCE_TRACKING,
  ANNOUNCE_TURN_FONT_PX,
  ANNOUNCE_TURN_MS,
  ANNOUNCE_TURN_TRACKING,
  ANNOUNCE_TURN_ZOOM_MS,
  ANNOUNCE_WASH,
  ANNOUNCE_ZOOM_FROM,
  ANNOUNCE_ZOOM_MS,
  SPECIES,
  UI_FONT,
} from '../config.js';
import { playerName } from '../dom.js';
import { playTurnGained, playTurnLost } from '../audio.js';
import { drawTracked } from './wasted.js';

/** How long either beat takes to leave. In fast, out slow, as the caption is. */
const OUT_MS = 280;

/** The round that has just been called. It states itself; nothing else reads it. */
export function announceRound(n) {
  state.announce = { n, at: state.clock };
}

/**
 * The nudge for whoever's turn it has just become.
 *
 * Derived every frame instead of being called from the five places that hand a turn
 * over — a miss, a turn lost to the clock, a defence spent, the top of a round, and a
 * match loaded from a code — for the same reason the turn clock is derived: a funnel
 * that has to be remembered is a funnel that gets a sixth caller and forgets. What
 * makes a turn a NEW turn is the pair (turn, when the turn started), and the second
 * half of that is a different number every time, so this says a turn once however
 * often it runs.
 *
 * Nothing about the saying is kept, only whose turn it is: the name, the words and
 * the colour are all worked out at drawing time, so a change of language under the
 * caption changes the caption.
 */
let calledTurn = null;
/** The turn whose cue has already been played. Its own key: the two are due together. */
let cuePlayed = null;

export function watchTurnCall() {
  const key = `${state.turn}:${state.turnStartedAt}`;

  if (key !== calledTurn) {
    calledTurn = key;
    // A turn of the machine's is not announced — there is nobody there to take it —
    // but it is still cued: the sound belongs to the player who has just been told
    // the board is not his any more.
    state.turnCall = { side: state.turn, at: state.clock, machine: state.turn === 1 && state.cpuOn };
  }

  // The cue waits for the round call exactly as the caption does, and for the same
  // reason: two things said about one turn land together or they are two things.
  const due = turnCallDue();
  if (due === null || cuePlayed === key || state.clock < due) return;

  // Never over the front end, where the turn it is about is not on screen at all.
  if (state.screen !== 'game' && state.screen !== 'pause') return;

  cuePlayed = key;
  if (state.turnCall.machine) playTurnLost();
  else playTurnGained();
}

/**
 * How far into the announcement we are, or null when there is nothing to show.
 *
 * Derived from the clock rather than counted frame by frame, for the same reason
 * the kill caption is: a pause must not leave ROUND 3 hanging over the menu for
 * as long as the pause lasted.
 */
function elapsed() {
  const a = state.announce;
  if (!a) return null;

  const ms = state.clock - a.at;
  return ms < 0 || ms > ANNOUNCE_ROUND_MS + ANNOUNCE_FIGHT_MS ? null : ms;
}

/**
 * Lettering at a size that settles instead of simply appearing: it starts over
 * size and comes in. Nothing in this game arrives at once, and a caption that
 * does reads as a stalled frame rather than as a decision.
 */
function punch(text, cx, cy, px, from, ms, zoomMs, tracking = ANNOUNCE_TRACKING, fill = null) {
  const scale = from + (1 - from) * Math.min(1, ms / zoomMs);

  ctx.save();
  ctx.translate(cx, cy);
  ctx.scale(scale, scale);
  ctx.font = `900 ${px}px ${UI_FONT}`;
  // `fill` is passed through rather than set on the context, because the thing that
  // paints the letters reads it from its own argument. Leaving it out keeps the
  // caller's white, which is what the round call has always been drawn in.
  if (fill) drawTracked(text, 0, 0, tracking, fill);
  else drawTracked(text, 0, 0, tracking);
  ctx.restore();
}

/**
 * When this turn's call is due, or null when there is nothing waiting.
 *
 * A round announcement holds it back. ROUND 3 and FIGHT are already saying something
 * with the middle of the screen and with the ear, and a nudge that lands on the far
 * side of FIGHT lands exactly when the player has stopped reading and started looking
 * for the gorillas. Both the caption and the cue are due at that same moment.
 */
function turnCallDue() {
  const c = state.turnCall;
  if (!c) return null;

  const r = state.announce;
  return r ? Math.max(c.at, r.at + ANNOUNCE_ROUND_MS + ANNOUNCE_FIGHT_MS) : c.at;
}

/**
 * How far into the caption we are, or null when there is none to show. A turn the CPU
 * has taken is cued and never captioned: it would be a caption about a player who is
 * not there.
 */
function turnCallElapsed() {
  const c = state.turnCall;
  if (!c || c.machine) return null;

  const ms = state.clock - turnCallDue();
  return ms < 0 || ms > ANNOUNCE_TURN_MS ? null : ms;
}

/**
 * Draws the round call. Two beats, and says whether it had anything to say.
 *
 * The number comes up over a darkened city so the lettering has something to sit
 * on; FIGHT lands on the city itself with no wash, because by then there is a fight
 * to look at and hiding it would be the wrong way round.
 */
function drawRoundCall() {
  const ms = elapsed();
  if (ms === null) return false;

  const cx = W / 2;
  const cy = H / 2 - 30;

  ctx.save();
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';

  if (ms <= ANNOUNCE_ROUND_MS) {
    const alpha = Math.min(1, ms / 90, (ANNOUNCE_ROUND_MS - ms) / OUT_MS);

    ctx.globalAlpha = alpha * ANNOUNCE_WASH;
    ctx.fillStyle = '#0b1020';
    ctx.fillRect(0, 0, W, H);

    ctx.globalAlpha = alpha;
    ctx.fillStyle = '#f4f6ff';
    ctx.shadowColor = '#3a6cff';
    ctx.shadowBlur = 26;
    punch(t('announce.round', { n: state.announce.n }), cx, cy,
      ANNOUNCE_FONT_PX, ANNOUNCE_ZOOM_FROM, ms, ANNOUNCE_ZOOM_MS);
  } else {
    const at = ms - ANNOUNCE_ROUND_MS;
    const alpha = Math.min(1, at / 70, (ANNOUNCE_FIGHT_MS - at) / 240);

    ctx.globalAlpha = Math.max(0, alpha);
    ctx.fillStyle = '#ffd24a';
    ctx.shadowColor = '#ff3b1f';
    ctx.shadowBlur = 30;
    punch(t('announce.fight'), cx, cy, ANNOUNCE_FIGHT_FONT_PX, 1.3, at, 130);
  }

  ctx.restore();
  return true;
}

/**
 * Draws the nudge: whose turn it has just become, in that gorilla's own colour.
 *
 * One beat, no wash, and two thirds the size of the round call, because this one is
 * said once a turn rather than once a round. The colour is the whole trick — which of
 * the two of you it means is answered before the words are read.
 */
function drawTurnCall() {
  const ms = turnCallElapsed();
  if (ms === null) return;

  const side = state.turnCall.side;
  const look = state.looks[side];
  const species = SPECIES[look ? look.species : side] || SPECIES[0];

  // In fast, out slow, as the round call is: a caption that appears at once reads as
  // a stalled frame rather than as a decision.
  const alpha = Math.min(1, ms / 90, (ANNOUNCE_TURN_MS - ms) / OUT_MS);
  if (alpha <= 0) return;

  ctx.save();
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.globalAlpha = alpha;
  ctx.shadowColor = species.body;
  ctx.shadowBlur = 22;
  // The colour travels as an argument and not as `ctx.fillStyle`: drawTracked sets
  // its own fill, and used to ignore whatever the caller had put on the context.
  punch(t('announce.turn', { name: playerName(side) }), W / 2, H / 2 - 30,
    ANNOUNCE_TURN_FONT_PX, 1.28, ms, ANNOUNCE_TURN_ZOOM_MS, ANNOUNCE_TURN_TRACKING,
    species.belly);
  ctx.restore();
}

/**
 * The whole voice, drawn last of all in the frame.
 *
 * The round call and the nudge, in that order and never at once: while the round
 * call is talking the nudge is still waiting its turn and has nothing to say yet.
 *
 * Never over the front end either. These are things said about a match, and there may
 * not be one behind the menu — the same reason the HUD is gated where it is.
 */
export function drawAnnouncement() {
  if (state.screen !== 'game' && state.screen !== 'pause') return;
  if (!drawRoundCall()) drawTurnCall();
}
